import { FirebaseError } from 'firebase/app';
import {
  FacebookAuthProvider,
  GoogleAuthProvider,
  getRedirectResult,
  sendSignInLinkToEmail,
  signInWithCredential,
  signInWithCustomToken,
  signInWithEmailLink,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  isSignInWithEmailLink,
  type ActionCodeSettings,
  type AuthProvider,
  type UserCredential,
} from 'firebase/auth';
import { facebookProvider, getFirebaseAuth, googleProvider } from '../lib/firebase';
import {
  oauthUsesRedirect,
} from '../lib/authDomain';
import { siteUrl } from '@/config/site';
import { apiFetch, authFetch, ApiError } from '@/platform/lib/api';
import { JoinGateError } from './joinGate';
import { persistLoginReturnTo } from './loginReturnTo';
import {
  e2eVerifiedEmailSession,
  shouldSkipFirebaseEmailCredentialApply,
  type VerifiedEmailSession,
} from './emailCredentialApply';
import { persistCreateOwnerEmail } from '@/platform/saas/createOwnerEmailCookie';
import { attachGuestAfterLogin } from '@/platform/commerce/api';
import { establishSession } from './establishSession';
import {
  completeRedirectResultOnce,
  hasPendingFirebaseRedirect,
  shouldSkipOAuthRedirectCompletion,
} from './oauthRedirect';
import {
  RECAPTCHA_LOGIN_ACTION,
  annotateLoginRecaptcha,
  verifyRecaptchaWithBackend,
  warmRecaptchaEnterprise,
} from './recaptchaEnterprise';
import { resolveEmailForEmailLinkSignIn } from './emailLinkSignIn';

export const EMAIL_FOR_SIGN_IN_KEY = 'emailForSignIn';

/** @deprecated Auth is modal + API on the current host — never page-navigate to apex. */
export function assignSlugAuthBounce(): boolean {
  return false;
}


export type OAuthProviderId = 'google' | 'facebook';

/** Preload reCAPTCHA Enterprise and verify with API (completes GCP key setup). */
export function ensureRecaptchaConfig(): Promise<void> {
  return warmRecaptchaEnterprise();
}

export function formatAuthError(err: unknown): string {
  if (err instanceof JoinGateError) {
    return err.message;
  }
  if (err instanceof ApiError) {
    if (err.status === 404) {
      return 'This community API does not support that sign-in method yet.';
    }
    return err.message || `Sign-in failed (${err.status})`;
  }
  if (err instanceof FirebaseError) {
    switch (err.code) {
      case 'auth/popup-closed-by-user':
        return 'Sign-in cancelled.';
      case 'auth/popup-blocked':
        return 'Popup blocked. Allow popups for this site, or try again.';
      case 'auth/unauthorized-domain':
        return 'This domain is not authorized for sign-in.';
      case 'auth/account-exists-with-different-credential':
        return 'An account already exists with this email using a different sign-in method.';
      case 'auth/operation-not-allowed':
        return 'This sign-in method is not enabled.';
      default:
        return err.message;
    }
  }
  return err instanceof Error ? err.message : 'Sign-in failed';
}

function oauthProviderFor(id: OAuthProviderId): AuthProvider {
  return id === 'google' ? googleProvider : facebookProvider;
}

function safeReturnToPath(returnTo?: string): string {
  return returnTo && returnTo.startsWith('/') ? returnTo : '/';
}

function actionCodeSettings(returnTo?: string): ActionCodeSettings {
  // Prefer the current Host so soft-middle incubating slugs get a matching continueUrl.
  const origin =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : '';
  const dest = safeReturnToPath(returnTo);
  const completePath = `/login/complete/?returnTo=${encodeURIComponent(dest)}`;
  return {
    url: origin ? `${origin.replace(/\/$/, '')}${completePath}` : siteUrl(completePath),
    handleCodeInApp: true,
  };
}

/** Send passwordless sign-in / sign-up link (protected by reCAPTCHA Enterprise).
 * @deprecated Prefer startEmailOTP / verifyEmailOTP (Gmail DWD via platform API). Kept for invite continue URLs. */
export type SendMagicLinkOptions = {
  /** When true (login default), block new-member links if seat cap is full. Step-up passes false. */
  checkSeatCapacity?: boolean;
  /** Path after /login/complete (e.g. /saas/start/?slug=…). Also mirrored in sessionStorage. */
  returnTo?: string;
};

async function assertSeatCapacityAllowsJoin(): Promise<void> {
  try {
    const cap = await apiFetch<{ enabled?: boolean; canAddMember?: boolean }>('/users/seat-capacity');
    if (cap.enabled && cap.canAddMember === false) {
      throw new Error(
        'Member limit reached. The host must accept the next growth step before new members can join.',
      );
    }
  } catch (err) {
    if (err instanceof Error && err.message.startsWith('Member limit reached')) {
      throw err;
    }
  }
}

/** Request a 6-digit email OTP (delivered via Workspace Gmail DWD / outbox). */
export async function startEmailOTP(email: string, opts?: SendMagicLinkOptions): Promise<void> {
  window.localStorage.setItem(EMAIL_FOR_SIGN_IN_KEY, email);
  const returnTo = safeReturnToPath(opts?.returnTo);
  persistLoginReturnTo(returnTo);
  if (!shouldSkipFirebaseEmailCredentialApply()) {
    if (opts?.checkSeatCapacity !== false) {
      await assertSeatCapacityAllowsJoin();
    }
    await verifyRecaptchaWithBackend(RECAPTCHA_LOGIN_ACTION, {
      email,
      persistAssessment: true,
    });
  }
  await authFetch<{ ok?: boolean }>('/auth/email-otp/start', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

/** Exchange email + OTP for Firebase custom token and sign in. */
export async function verifyEmailOTPAndSignIn(email: string, code: string): Promise<VerifiedEmailSession> {
  return verifyEmailCredentialAndSignIn({
    email: email.trim(),
    fetchCustomToken: async () => {
      const data = await authFetch<{ customToken: string }>('/auth/email-otp/verify', {
        method: 'POST',
        body: JSON.stringify({ email: email.trim(), code: code.trim() }),
      });
      return data.customToken ?? '';
    },
  });
}

/** Consume a member-invite join token on this host (same-origin community API, not apex OTP). */
export async function verifyInviteLinkAndSignIn(email: string, token: string): Promise<VerifiedEmailSession> {
  return verifyEmailCredentialAndSignIn({
    email: email.trim(),
    replaceSession: true,
    fetchCustomToken: async () => {
      const data = await apiFetch<{ customToken: string }>('/auth/invite-link/verify', {
        method: 'POST',
        body: JSON.stringify({ email: email.trim(), token: token.trim() }),
      });
      return data.customToken ?? '';
    },
  });
}

async function verifyEmailCredentialAndSignIn(opts: {
  email: string;
  fetchCustomToken: () => Promise<string>;
  replaceSession?: boolean;
}): Promise<VerifiedEmailSession> {
  const e2eSession = e2eVerifiedEmailSession(opts.email);
  if (opts.replaceSession && !e2eSession) {
    const auth = getFirebaseAuth();
    if (auth.currentUser) {
      await signOut(auth);
    }
  }
  const customToken = (await opts.fetchCustomToken()).trim();
  if (!customToken) {
    throw new Error('Verify returned no custom token');
  }
  if (e2eSession) {
    window.localStorage.removeItem(EMAIL_FOR_SIGN_IN_KEY);
    persistCreateOwnerEmail(opts.email);
    return e2eSession;
  }
  const cred = await signInWithCustomToken(getFirebaseAuth(), customToken);
  window.localStorage.removeItem(EMAIL_FOR_SIGN_IN_KEY);
  persistCreateOwnerEmail(opts.email);
  await annotateLoginRecaptcha({
    loginSucceeded: true,
    email: opts.email,
    accountId: cred.user.uid,
  });
  return { uid: cred.user.uid, email: opts.email };
}

export async function completeInviteJoinLink(email: string, token: string): Promise<void> {
  await verifyInviteLinkAndSignIn(email, token);
  if (shouldSkipFirebaseEmailCredentialApply()) {
    return;
  }
  await establishSession();
  try {
    await attachGuestAfterLogin();
  } catch {
    /* best-effort */
  }
}

export async function sendMagicLinkEmail(email: string, opts?: SendMagicLinkOptions): Promise<void> {
  window.localStorage.setItem(EMAIL_FOR_SIGN_IN_KEY, email);
  const returnTo = safeReturnToPath(opts?.returnTo);
  // Same-browser fallback when continueUrl query is stripped or opened without ?returnTo=.
  persistLoginReturnTo(returnTo);
  if (!shouldSkipFirebaseEmailCredentialApply()) {
    if (opts?.checkSeatCapacity !== false) {
      await assertSeatCapacityAllowsJoin();
    }
    await verifyRecaptchaWithBackend(RECAPTCHA_LOGIN_ACTION, {
      email,
      persistAssessment: true,
    });
    const auth = getFirebaseAuth();
    await sendSignInLinkToEmail(auth, email, actionCodeSettings(returnTo));
  }
}

/**
 * OAuth sign-in. Product slugs navigate to ops (only authorized Auth host).
 * Ops / paydirt / localhost use Firebase popup or same-host redirect.
 */
export async function signInWithOAuth(
  providerId: OAuthProviderId,
): Promise<UserCredential | null> {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const authDomain = process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN;

  const auth = getFirebaseAuth();
  const provider = oauthProviderFor(providerId);
  const useRedirect =
    typeof window !== 'undefined' &&
    process.env.NODE_ENV === 'production' &&
    !process.env.NEXT_PUBLIC_E2E_MOCK_UID &&
    oauthUsesRedirect({
      hostname,
      authDomain,
    });

  if (useRedirect) {
    await signInWithRedirect(auth, provider);
    return null;
  }

  return signInWithPopup(auth, provider);
}

/** Finish OAuth after signInWithRedirect. One-shot: remounts reuse the same promise. */
export async function completeOAuthRedirectSignIn(): Promise<{
  cred: UserCredential | null;
  hadPending: boolean;
  currentUser: unknown | null;
}> {
  if (shouldSkipOAuthRedirectCompletion()) {
    return { cred: null, hadPending: false, currentUser: null };
  }
  const hadPending =
    typeof window !== 'undefined' && hasPendingFirebaseRedirect(window.sessionStorage);
  const auth = getFirebaseAuth();
  const cred = await completeRedirectResultOnce(() => getRedirectResult(auth));
  return { cred, hadPending, currentUser: auth.currentUser };
}

export async function signInWithGoogle(): Promise<UserCredential> {
  const cred = await signInWithOAuth('google');
  if (!cred) {
    throw new Error('Redirecting to Google sign-in…');
  }
  return cred;
}

export async function signInWithFacebook(): Promise<UserCredential> {
  const cred = await signInWithOAuth('facebook');
  if (!cred) {
    throw new Error('Redirecting to Facebook sign-in…');
  }
  return cred;
}

/** Sign in via official Facebook Login button (JS SDK access token). */
export async function signInWithFacebookAccessToken(accessToken: string): Promise<UserCredential> {
  const auth = getFirebaseAuth();
  return signInWithCredential(auth, FacebookAuthProvider.credential(accessToken));
}

/** Complete email link sign-in when user lands on /login/complete. Returns null if not an email link. */
export async function completeEmailLinkSignIn(
  linkUrl: string,
  emailHint?: string | null,
): Promise<UserCredential | null> {
  const auth = getFirebaseAuth();
  if (!isSignInWithEmailLink(auth, linkUrl)) {
    return null;
  }
  // Invite continue URLs carry ?email= for the invitee — must win over stale emailForSignIn.
  const email = resolveEmailForEmailLinkSignIn({
    linkUrl,
    emailHint,
    storedEmail: window.localStorage.getItem(EMAIL_FOR_SIGN_IN_KEY),
  });
  if (!email) {
    throw new Error('EMAIL_REQUIRED_FOR_LINK');
  }
  window.localStorage.setItem(EMAIL_FOR_SIGN_IN_KEY, email);
  try {
    const cred = await signInWithEmailLink(auth, email, linkUrl);
    window.localStorage.removeItem(EMAIL_FOR_SIGN_IN_KEY);
    await annotateLoginRecaptcha({
      loginSucceeded: true,
      email,
      accountId: cred.user.uid,
    });
    return cred;
  } catch (err) {
    await annotateLoginRecaptcha({ loginSucceeded: false, email });
    throw err;
  }
}

export { GoogleAuthProvider, isSignInWithEmailLink };
